import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft, DollarSign, Globe, RotateCcw, Loader2, Download, Upload, Zap, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatMoney } from "../lib/money";
import AdminFareTable from "../components/AdminFareTable";

/**
 * Standalone admin-only Prices page.
 *
 * Shows every (country × category) rate row in one place with a summary panel
 * ("Israel taxi · ₪5 base + ₪2.5/km + ₪0.5/min") so the admin can list current
 * prices at a glance and drill into `AdminFareTable` to edit a specific country.
 */
export default function AdminPrices() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("taxi");
  const [busy, setBusy] = useState(false);
  const [showSurge, setShowSurge] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [csvPreview, setCsvPreview] = useState(null); // { csv_text, rows_upserted, preview, errors }
  const [csvBusy, setCsvBusy] = useState(false);

  const exportCsv = () => {
    // Server sends `Content-Disposition: attachment` — hit the URL with token
    // via a temporary anchor so the browser saves the file.
    api.get("/admin/fare-table.csv", { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "drivons_fare_table.csv";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  const importCsv = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const csv_text = await file.text();
      // Dry-run first to build the diff preview modal
      const { data: dry } = await api.post("/admin/fare-table/import", { csv_text, dry_run: true });
      const preview = dry.preview || [];
      const changed = preview.filter((p) => (p.changed_fields || []).length > 0);
      const unchanged = preview.filter((p) => (p.changed_fields || []).length === 0);
      setCsvPreview({
        csv_text,
        rows_total: preview.length,
        rows_changed: changed.length,
        rows_unchanged: unchanged.length,
        preview,
        errors: dry.errors || [],
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      e.target.value = "";
    }
  };

  const confirmCsvImport = async () => {
    if (!csvPreview) return;
    setCsvBusy(true);
    try {
      const { data } = await api.post("/admin/fare-table/import", { csv_text: csvPreview.csv_text });
      toast.success(t("admin.prices.importDone", "Imported {{n}} rows", { n: data.rows_upserted }));
      if ((data.errors || []).length) toast.error(data.errors.join(" · "));
      setCsvPreview(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCsvBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fare-table");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetAll = async () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(t("admin.prices.resetAllConfirm", "Reset ALL price overrides back to code defaults?"))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/fare-table/reset", { all: true });
      toast.success(t("admin.prices.resetDone", `Cleared ${data.cleared} overrides`));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen bg-obsidian text-white flex items-center justify-center p-8">
        <div className="max-w-md text-center">
          <div className="text-2xl font-black">{t("admin.prices.forbiddenTitle", "Admin only")}</div>
          <div className="text-sm text-white/60 mt-2">{t("admin.prices.forbidden", "This price management page is reserved for administrators.")}</div>
          <Link to="/" className="mt-4 inline-block text-sm font-black text-electric-teal">← {t("common.home", "Home")}</Link>
        </div>
      </div>
    );
  }

  const catRows = rows
    .filter((r) => r.category === category)
    .sort((a, b) => a.country.localeCompare(b.country));

  const overridden = catRows.filter((r) => r.is_overridden).length;

  return (
    <div className="min-h-screen bg-obsidian text-white pb-24">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5" data-testid="admin-prices-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin" className="text-[11px] font-black uppercase text-white/50 inline-flex items-center gap-1 hover:text-white" data-testid="admin-prices-back">
              <ArrowLeft size={12} /> {t("admin.prices.backToDashboard", "Back to dashboard")}
            </Link>
            <div className="text-[10px] uppercase tracking-[0.24em] font-black text-electric-teal mt-2">{t("admin.prices.eyebrow", "Pricing engine")}</div>
            <h1 className="text-3xl sm:text-4xl font-black mt-1">{t("admin.prices.title", "Prices by country")}</h1>
            <p className="text-white/60 mt-1.5 text-sm">
              {t("admin.prices.subtitle", "Set the fare formula for every country you serve. Every customer estimate and order total is computed from this table.")}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={load} disabled={loading || busy}
              className="rounded-full px-3.5 py-2 text-xs font-black bg-white/[0.06] border border-white/10 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="admin-prices-reload">
              {loading ? <Loader2 size={12} className="animate-spin" /> : null}
              {t("admin.prices.reload", "Reload")}
            </button>
            <button onClick={exportCsv}
              className="rounded-full px-3.5 py-2 text-xs font-black bg-white/[0.06] border border-white/10 text-white inline-flex items-center gap-1.5"
              data-testid="admin-prices-export">
              <Download size={12} /> {t("admin.prices.exportCsv", "Export CSV")}
            </button>
            <label
              className="cursor-pointer rounded-full px-3.5 py-2 text-xs font-black bg-white/[0.06] border border-white/10 text-white inline-flex items-center gap-1.5"
              data-testid="admin-prices-import-label">
              <Upload size={12} /> {t("admin.prices.importCsv", "Import CSV")}
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} data-testid="admin-prices-import-input" />
            </label>
            <button onClick={() => setShowSurge((v) => !v)}
              className={`rounded-full px-3.5 py-2 text-xs font-black inline-flex items-center gap-1.5 border ${showSurge ? "bg-amber-400/15 text-amber-200 border-amber-400/40" : "bg-white/[0.06] border-white/10 text-white"}`}
              data-testid="admin-prices-surge-btn">
              <Zap size={12} /> {t("admin.prices.surge", "Surge")}
            </button>
            <button onClick={() => setShowHistory((v) => !v)}
              className={`rounded-full px-3.5 py-2 text-xs font-black inline-flex items-center gap-1.5 border ${showHistory ? "bg-electric-teal/15 text-electric-teal border-electric-teal/40" : "bg-white/[0.06] border-white/10 text-white"}`}
              data-testid="admin-prices-history-btn">
              <History size={12} /> {t("admin.prices.history", "History")}
            </button>
            <button onClick={resetAll} disabled={loading || busy}
              className="rounded-full px-3.5 py-2 text-xs font-black bg-red-500/10 border border-red-500/40 text-red-300 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="admin-prices-reset-all">
              <RotateCcw size={12} /> {t("admin.prices.resetAll", "Reset all")}
            </button>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
          {Array.from(new Set(rows.map((r) => r.category))).sort().map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-black border-2 ${category === c ? "bg-electric-teal text-obsidian border-electric-teal" : "bg-white/[0.04] text-white/70 border-white/10"}`}
              data-testid={`admin-prices-cat-${c}`}
            >
              {t(`categories.${c}`, c)}
            </button>
          ))}
        </div>

        {/* Summary row */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-4 flex-wrap" data-testid="admin-prices-summary">
          <div className="inline-flex items-center gap-2">
            <DollarSign size={14} className="text-electric-teal" />
            <span className="text-sm font-black">
              {t("admin.prices.countCategory", "{{count}} countries", { count: catRows.length })}
            </span>
          </div>
          {overridden > 0 && (
            <span className="text-[10px] font-black uppercase tracking-widest bg-electric-teal/15 text-electric-teal rounded-full px-2 py-1">
              {t("admin.prices.overriddenCount", "{{n}} custom", { n: overridden })}
            </span>
          )}
        </div>

        {/* Surge + History drawers */}
        {showSurge && <SurgePanel />}
        {showHistory && <HistoryPanel />}

        {/* Country list */}
        {loading ? (
          <div className="flex justify-center p-10 text-white/50"><Loader2 size={16} className="animate-spin" /></div>
        ) : (
          <div className="space-y-2" data-testid="admin-prices-country-list">
            {catRows.map((row) => (
              <CountryRow key={`${row.country}-${row.category}`} row={row} />
            ))}
          </div>
        )}

        {/* Full editor for the currently selected country */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <AdminFareTable />
        </div>

        {csvPreview && (
          <CsvDiffModal
            data={csvPreview}
            busy={csvBusy}
            onCancel={() => setCsvPreview(null)}
            onConfirm={confirmCsvImport}
          />
        )}
      </div>
    </div>
  );
}

function CsvDiffModal({ data, busy, onCancel, onConfirm }) {
  const { t } = useTranslation();
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="csv-diff-modal"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] rounded-2xl bg-obsidian border border-white/10 shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-white/10 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black text-electric-teal">
              {t("admin.prices.csvDiffEyebrow", "CSV import preview")}
            </div>
            <h2 className="text-xl font-black text-white mt-1">
              {t("admin.prices.csvDiffTitle", "Review changes before applying")}
            </h2>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="text-white/50 hover:text-white text-2xl leading-none disabled:opacity-40"
            data-testid="csv-diff-close"
            aria-label={t("common.close", "Close")}
          >×</button>
        </div>

        <div className="p-5 flex items-center gap-3 flex-wrap border-b border-white/10 bg-white/[0.02]">
          <div className="rounded-full px-3 py-1.5 text-xs font-black bg-electric-teal/15 text-electric-teal border border-electric-teal/40" data-testid="csv-diff-count-changed">
            {t("admin.prices.csvDiffChanged", "{{n}} to change", { n: data.rows_changed })}
          </div>
          <div className="rounded-full px-3 py-1.5 text-xs font-black bg-white/[0.06] text-white/60 border border-white/10" data-testid="csv-diff-count-unchanged">
            {t("admin.prices.csvDiffUnchanged", "{{n}} unchanged", { n: data.rows_unchanged })}
          </div>
          <div className="rounded-full px-3 py-1.5 text-xs font-black bg-white/[0.06] text-white/60 border border-white/10" data-testid="csv-diff-count-total">
            {t("admin.prices.csvDiffTotal", "{{n}} total", { n: data.rows_total })}
          </div>
          {data.errors.length > 0 && (
            <div className="rounded-full px-3 py-1.5 text-xs font-black bg-red-500/15 text-red-300 border border-red-500/40" data-testid="csv-diff-count-errors">
              {t("admin.prices.csvDiffErrors", "{{n}} errors", { n: data.errors.length })}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-2 min-h-0" data-testid="csv-diff-body">
          {data.preview.length === 0 ? (
            <div className="text-sm text-white/60 text-center py-8">
              {t("admin.prices.csvDiffEmpty", "No parseable rows in this file.")}
            </div>
          ) : (
            data.preview.map((row, idx) => {
              const changed = (row.changed_fields || []).length > 0;
              return (
                <div
                  key={`${row.country}-${row.category}-${idx}`}
                  className={`rounded-xl p-3 border text-xs ${changed ? "border-electric-teal/30 bg-electric-teal/[0.05]" : "border-white/10 bg-white/[0.02]"}`}
                  data-testid={`csv-diff-row-${row.country}-${row.category}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-black text-white">
                      {row.country} · {row.category}
                    </div>
                    <span
                      className={`text-[9px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 ${changed ? "bg-electric-teal/20 text-electric-teal" : "bg-white/10 text-white/50"}`}
                      data-testid={`csv-diff-row-status-${row.country}-${row.category}`}
                    >
                      {changed ? t("admin.prices.csvDiffRowChanged", "change") : t("admin.prices.csvDiffRowSame", "no change")}
                    </span>
                  </div>
                  {changed && (
                    <div className="mt-1.5 space-y-0.5">
                      {row.changed_fields.map((f) => (
                        <div key={f} className="text-white/70">
                          <span className="uppercase text-[9px] font-black text-white/40">{f}</span>:{" "}
                          <span className="text-white/50">{String(row.before?.[f] ?? "—")}</span>{" → "}
                          <span className="text-white font-black">{String(row.after?.[f] ?? "—")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
          {data.errors.length > 0 && (
            <div className="rounded-xl p-3 border border-red-500/40 bg-red-500/[0.06] space-y-0.5" data-testid="csv-diff-errors">
              <div className="text-[10px] uppercase font-black text-red-300 tracking-widest mb-1">
                {t("admin.prices.csvDiffErrorsTitle", "Errors")}
              </div>
              {data.errors.map((err, i) => (
                <div key={i} className="text-xs text-red-200/90 font-mono">{err}</div>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-white/10 flex items-center justify-end gap-2 bg-white/[0.02]">
          <button
            onClick={onCancel}
            disabled={busy}
            className="rounded-full px-4 py-2 text-xs font-black bg-white/[0.06] border border-white/10 text-white hover:bg-white/[0.10] disabled:opacity-40"
            data-testid="csv-diff-cancel"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || data.rows_changed === 0}
            className="rounded-full px-4 py-2 text-xs font-black bg-electric-teal text-obsidian border border-electric-teal hover:bg-electric-teal/90 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="csv-diff-confirm"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {data.rows_changed > 0
              ? t("admin.prices.csvDiffApply", "Apply {{n}} changes", { n: data.rows_changed })
              : t("admin.prices.csvDiffNothing", "Nothing to apply")}
          </button>
        </div>
      </div>
    </div>
  );
}

function CountryRow({ row }) {
  const { t } = useTranslation();
  const currency = row.currency;
  const overridden = row.is_overridden;
  return (
    <div className={`rounded-xl p-3 border flex items-center gap-3 flex-wrap ${overridden ? "border-electric-teal/40 bg-electric-teal/[0.06]" : "border-white/10 bg-white/[0.03]"}`} data-testid={`admin-price-row-${row.country}-${row.category}`}>
      <div className="w-14 shrink-0">
        <div className="text-[10px] uppercase font-black text-white/40">{t("admin.prices.country", "Country")}</div>
        <div className="text-sm font-black text-white flex items-center gap-1">
          <Globe size={12} className="text-white/40" /> {row.country}
        </div>
      </div>
      <PriceStat label={t("admin.prices.base", "Base")} value={formatMoney(row.base_fare, currency)} />
      <PriceStat label={`${t("admin.prices.perKm", "per km")}`} value={formatMoney(row.per_km_rate, currency)} />
      <PriceStat label={`${t("admin.prices.perMin", "per min")}`} value={row.per_minute_rate ? formatMoney(row.per_minute_rate, currency) : "—"} />
      <PriceStat label={t("admin.prices.min", "Min fare")} value={row.min_fare ? formatMoney(row.min_fare, currency) : "—"} />
      <div className="ms-auto">
        {overridden ? (
          <span className="text-[10px] font-black uppercase tracking-widest bg-electric-teal/20 text-electric-teal rounded-full px-2 py-0.5" data-testid={`admin-price-badge-${row.country}`}>
            {t("admin.prices.custom", "custom")}
          </span>
        ) : (
          <span className="text-[10px] font-black uppercase tracking-widest text-white/40" data-testid={`admin-price-badge-${row.country}`}>
            {t("admin.prices.default", "default")}
          </span>
        )}
      </div>
    </div>
  );
}

function PriceStat({ label, value }) {
  return (
    <div className="min-w-[68px]">
      <div className="text-[10px] uppercase font-black text-white/40">{label}</div>
      <div className="text-sm font-black text-white truncate">{value}</div>
    </div>
  );
}

function SurgePanel() {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/surge").then((r) => setCfg(r.data)).catch(() => setCfg({ active: false, multiplier: 1.5, peak_hours: [] }));
  }, []);

  if (!cfg) return null;

  const toggleHour = (h) => {
    const has = cfg.peak_hours.includes(h);
    setCfg({ ...cfg, peak_hours: has ? cfg.peak_hours.filter((x) => x !== h) : [...cfg.peak_hours, h].sort((a, b) => a - b) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/surge", cfg);
      setCfg(data);
      toast.success(t("admin.prices.surgeSaved", "Surge settings saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.05] p-4 space-y-3" data-testid="admin-surge-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase font-black text-amber-300 tracking-widest">{t("admin.prices.surge", "Surge pricing")}</div>
          <div className="text-sm font-black text-white mt-0.5">{t("admin.prices.surgeSubtitle", "Auto-multiply every fare during peak hours")}</div>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-black">
          <input type="checkbox" checked={!!cfg.active} onChange={(e) => setCfg({ ...cfg, active: e.target.checked })} data-testid="admin-surge-active" />
          {cfg.active ? t("admin.prices.surgeOn", "Surge is ON") : t("admin.prices.surgeOff", "Surge is OFF")}
        </label>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-[11px] font-black text-white/70">
          {t("admin.prices.surgeMultiplier", "Multiplier")}: <span className="text-amber-300">×{Number(cfg.multiplier).toFixed(1)}</span>
        </label>
        <input type="range" min="1.0" max="3.0" step="0.1" value={cfg.multiplier}
          onChange={(e) => setCfg({ ...cfg, multiplier: Number(e.target.value) })}
          className="flex-1 min-w-[200px]" data-testid="admin-surge-multiplier" />
      </div>
      <div>
        <div className="text-[10px] uppercase font-black text-white/50 tracking-widest mb-1.5">{t("admin.prices.peakHoursUtc", "Peak hours (UTC)")}</div>
        <div className="grid grid-cols-8 sm:grid-cols-12 gap-1">
          {Array.from({ length: 24 }, (_, h) => (
            <button key={h} onClick={() => toggleHour(h)}
              className={`text-[10px] font-black rounded-md py-1.5 border ${cfg.peak_hours.includes(h) ? "bg-amber-400 text-obsidian border-amber-400" : "bg-white/[0.03] border-white/10 text-white/60"}`}
              data-testid={`admin-surge-hr-${h}`}>
              {String(h).padStart(2, "0")}
            </button>
          ))}
        </div>
      </div>
      <button onClick={save} disabled={saving}
        className="rounded-full bg-amber-400 text-obsidian px-4 py-2 text-xs font-black inline-flex items-center gap-1.5 disabled:opacity-50"
        data-testid="admin-surge-save">
        {saving && <Loader2 size={12} className="animate-spin" />}
        {t("admin.prices.saveSurge", "Save surge")}
      </button>
    </div>
  );
}

function HistoryPanel() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/fare-history", { params: { limit: 50 } })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="rounded-2xl border border-electric-teal/30 bg-electric-teal/[0.05] p-4 space-y-2" data-testid="admin-history-panel">
      <div className="text-[10px] uppercase font-black text-electric-teal tracking-widest">{t("admin.prices.historyTitle", "Fare change log")}</div>
      {loading ? (
        <div className="text-xs text-white/50 inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> …</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-white/50">{t("admin.prices.historyEmpty", "No fare changes yet.")}</div>
      ) : (
        <div className="space-y-1 max-h-72 overflow-y-auto">
          {rows.map((r) => (
            <div key={r.history_id} className="rounded-lg bg-white/[0.03] border border-white/10 p-2 text-[11px]" data-testid={`admin-history-row-${r.history_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-black text-white">
                  {r.country} · {r.category}
                  {r.source === "csv_import" && <span className="ms-2 text-[9px] uppercase tracking-widest bg-electric-teal/20 text-electric-teal rounded-full px-1.5 py-0.5">csv</span>}
                  {r.source === "revert" && <span className="ms-2 text-[9px] uppercase tracking-widest bg-amber-400/20 text-amber-300 rounded-full px-1.5 py-0.5">revert</span>}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-white/40">{(r.created_at || "").slice(0, 19).replace("T", " ")}</div>
                  {r.source !== "revert" && (
                    <button
                      onClick={async () => {
                        // eslint-disable-next-line no-restricted-globals, no-alert
                        if (!confirm(`Revert ${r.country}/${r.category} back to the "before" snapshot of this entry?`)) return;
                        try {
                          await api.post(`/admin/fare-history/${r.history_id}/revert`);
                          toast.success(`Reverted ${r.country}/${r.category}`);
                          setRows((prev) => [...prev]); // trigger reload of nearby data
                          load();
                        } catch (e) {
                          toast.error(formatApiError(e));
                        }
                      }}
                      className="text-[9px] font-black uppercase bg-amber-400/15 text-amber-300 border border-amber-400/40 rounded-full px-2 py-0.5 hover:bg-amber-400/25"
                      data-testid={`admin-history-revert-${r.history_id}`}
                    >
                      ↶ revert
                    </button>
                  )}
                </div>
              </div>
              <div className="text-white/60 mt-0.5">
                {t("admin.prices.by", "by")} {r.admin_email || r.admin_id} — {t("admin.prices.changed", "changed")} {r.changed_fields.join(", ") || "—"}
              </div>
              {r.changed_fields.map((f) => (
                <div key={f} className="ms-1 text-white/50">
                  <span className="uppercase text-[9px] font-black text-white/40">{f}</span>: {String(r.before?.[f] ?? "—")} → <span className="text-white font-black">{String(r.after?.[f] ?? "—")}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
